import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function ApiPlayground() {
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [age, setAge] = useState('');
  const [primaryConcern, setPrimaryConcern] = useState('');
  const [includeAnnotatedImage, setIncludeAnnotatedImage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [requestTime, setRequestTime] = useState(null);

  const fileInputRef = useRef(null);

  const origin = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'https://my-skin.ai';
  }, []);

  const payloadPreview = useMemo(() => {
    return JSON.stringify(
      {
        imageData: imageData ? 'data:image/...base64' : '(upload required)',
        ...(age ? { age: parseInt(age, 10) } : {}),
        ...(primaryConcern ? { primaryConcern } : {}),
        includeAnnotatedImage
      },
      null,
      2
    );
  }, [imageData, age, primaryConcern, includeAnnotatedImage]);

  const curlPreview = useMemo(() => {
    return `curl -X POST ${origin}/api/v2/skin-analysis \\
  -H "Content-Type: application/json" \\
  -H "MySkinAI-API-Key: ${apiKey || 'msk_your_api_key_here'}" \\
  -d '{
    "imageData": "data:image/jpeg;base64,..."${age ? `,\n    "age": ${parseInt(age, 10) || ''}` : ''}${primaryConcern ? `,\n    "primaryConcern": "${primaryConcern}"` : ''},
    "includeAnnotatedImage": ${includeAnnotatedImage}
  }'`;
  }, [origin, apiKey, age, primaryConcern, includeAnnotatedImage]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setImageData(loadEvent.target.result);
      setImagePreview(loadEvent.target.result);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const clearAll = () => {
    setImageData(null);
    setImagePreview(null);
    setAge('');
    setPrimaryConcern('');
    setIncludeAnnotatedImage(true);
    setResponse(null);
    setError(null);
    setRequestTime(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setResponse(null);

    if (!apiKey.trim()) {
      setError('API key is required. Playground does not bypass approval.');
      return;
    }

    if (!imageData) {
      setError('Please upload an image');
      return;
    }

    setLoading(true);
    const start = Date.now();

    try {
      const res = await fetch('/api/v2/skin-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'MySkinAI-API-Key': apiKey.trim()
        },
        body: JSON.stringify({
          imageData,
          age: age ? parseInt(age, 10) : undefined,
          primaryConcern: primaryConcern || undefined,
          includeAnnotatedImage
        })
      });

      const data = await res.json();
      setRequestTime(Date.now() - start);
      setResponse({ status: res.status, data });

      if (!res.ok) {
        const errorMessage = data?.error?.message || data?.message || data?.error || 'Request failed';
        setError(`Error ${res.status}: ${errorMessage}`);
      }
    } catch (requestError) {
      setRequestTime(Date.now() - start);
      setError(requestError.message || 'Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-white">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gray-900 text-white font-bold flex items-center justify-center">M</div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">MySkinAI Playground</h1>
              <p className="text-xs text-gray-500">Interactive v2 API testing</p>
            </div>
          </Link>
          <div className="flex items-center space-x-3">
            <Link to="/api-docs" className="text-sm font-medium text-gray-700 hover:text-gray-900">Docs</Link>
            <Link to="/developers" className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">Developer Portal</Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <section className="rounded-3xl border border-gray-200 bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">API Sandbox</h2>
          <p className="text-sm text-gray-600 max-w-4xl">
            This sandbox sends real requests to <code className="bg-gray-100 px-1.5 py-0.5 rounded">/api/v2/skin-analysis</code>.
            Use an approved key to validate request shape and annotated image handling before integrating into production.
          </p>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="rounded-2xl border border-gray-200 bg-white p-6 space-y-5">
            <h3 className="text-lg font-semibold text-gray-900">Request</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API Key *</label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 pr-24"
                    placeholder="msk_your_api_key_here"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((value) => !value)}
                    className="absolute right-2 top-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image *</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-gray-300 transition-colors"
                >
                  {imagePreview ? (
                    <div className="space-y-3">
                      <img src={imagePreview} alt="Preview" className="max-h-56 mx-auto rounded-lg" />
                      <p className="text-xs text-gray-500">Click to replace image</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700 font-medium">Upload image</p>
                      <p className="text-xs text-gray-500">JPEG, PNG, WebP up to 5MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age (optional)</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    min="13"
                    max="120"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="35"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary Concern</label>
                  <select
                    value={primaryConcern}
                    onChange={(e) => setPrimaryConcern(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="">Select concern</option>
                    <option value="acne">Acne</option>
                    <option value="pigmentation">Pigmentation</option>
                    <option value="wrinkles">Wrinkles</option>
                    <option value="dark_circles">Dark circles</option>
                    <option value="pores">Pores</option>
                    <option value="dull_skin">Dull skin</option>
                    <option value="acne_scar">Acne scars</option>
                  </select>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-4 bg-gray-50">
                <label className="inline-flex items-center gap-3 text-sm text-gray-700 font-medium">
                  <input
                    type="checkbox"
                    checked={includeAnnotatedImage}
                    onChange={(e) => setIncludeAnnotatedImage(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Include inline annotated image in response
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Keep enabled to preview end-user overlays. Disable when testing smaller response payloads.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Send Request'}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>
            </form>

            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </section>

          <section className="space-y-6">
            <article className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Response</h3>
                <div className="flex items-center gap-3">
                  {requestTime !== null && <span className="text-xs text-gray-500">{requestTime}ms</span>}
                </div>
              </div>

              {!response ? (
                <p className="text-sm text-gray-500">No response yet.</p>
              ) : (
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      response.status === 200
                        ? 'bg-green-100 text-green-700'
                        : response.status === 401
                          ? 'bg-red-100 text-red-700'
                          : response.status === 429
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                      {response.status}
                    </span>
                    {response.data?.error?.code && (
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{response.data.error.code}</code>
                    )}
                  </div>

                  {response.data?.annotatedImage && (
                    <div className="rounded-xl border border-gray-200 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Annotated Image</p>
                      <img
                        src={response.data.annotatedImage}
                        alt="Annotated output"
                        className="max-h-64 w-auto rounded-lg mx-auto"
                      />
                    </div>
                  )}

                  <pre className="bg-gray-950 text-gray-100 rounded-xl p-4 text-sm overflow-auto max-h-[360px]">
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                </div>
              )}
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Payload Preview</h3>
              <pre className="bg-gray-100 rounded-xl p-4 text-xs overflow-auto">{payloadPreview}</pre>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">cURL Preview</h3>
              <pre className="bg-gray-950 text-gray-100 rounded-xl p-4 text-xs overflow-auto">{curlPreview}</pre>
            </article>
          </section>
        </div>
      </main>
    </div>
  );
}

export default ApiPlayground;
